/**
 * My account — what the signed-in person can see and change about their own login.
 *
 * A drawer off the profile menu rather than a screen in the registry, on purpose. Every registered screen
 * is guarded by a permission, and this must be reachable by an account that holds none: someone with no
 * role yet still needs to see who they are signed in as, set a password and enrol a second factor.
 *
 * Everything here is the caller's own data, and the database agrees: `api.me`, `api.my_access`, and
 * `api.login_activity` — whose RLS returns the caller's own rows without needing `iam.sessions.view`.
 * There is nothing on this panel that changes what the account may *do*. Status, roles, scope and the MFA
 * *requirement* belong to an administrator; what belongs to the person is their password and their factor.
 */
import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { KeyRound, LogOut, ShieldCheck } from 'lucide-react';
import { useSession } from '../lib/session';
import { supabase } from '../lib/supabase';
import { beginTotpEnrolment, passwordProblem, setPassword, signOutEverywhere, verifyTotp, type Enrolment } from '../lib/auth';
import { Drawer } from '../ui/overlays';
import { Button, Fact, FactGrid, SectionLabel, StatusBadge, Unknown, cx } from '../ui/primitives';
import { Field, Input } from '../ui/forms';
import { date as fmtDate, dateTime as fmtDateTime } from '../ui/format';

interface LoginEvent {
  id: string;
  event: string;
  occurred_at: string;
}

const EVENT_WORD: Record<string, string> = {
  SIGN_IN: 'Signed in', SIGN_OUT: 'Signed out', FAILED: 'Failed attempt', MFA_CHALLENGE: 'Second factor requested',
  MFA_PASSED: 'Second factor passed', LOCKED: 'Account locked', TOKEN_REFRESH: 'Session refreshed',
  SESSION_SEEN: 'Session opened', SESSIONS_REVOKED: 'Sessions ended',
};

export function MyAccountDrawer({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { me, access, aal, authEmail, refresh } = useSession();
  const queryClient = useQueryClient();

  const activity = useQuery({
    queryKey: ['my-login-activity'],
    enabled: open && Boolean(me?.user_account_id),
    queryFn: async () => {
      const { data, error } = await supabase
        .from('login_activity')
        .select('id,event,occurred_at')
        .eq('user_account_id', me!.user_account_id!)
        .order('occurred_at', { ascending: false })
        .limit(12);
      if (error) throw error;
      return data as LoginEvent[];
    },
  });

  return (
    <Drawer open={open} onClose={onClose} title="My account" width="30rem">
      <FactGrid columns={2}>
        <Fact label="Signed in as">{me?.email ?? authEmail}</Fact>
        <Fact label="Person">{me?.person?.display_name ?? <Unknown title="This login is not linked to a person record" />}</Fact>
        <Fact label="Organisation">{me?.organization?.name}</Fact>
        <Fact label="Account status">{me?.status}</Fact>
      </FactGrid>

      <section className="border-t border-line px-4 py-3">
        <SectionLabel>Roles and scope</SectionLabel>
        {(access?.grants ?? []).length === 0 ? (
          <p className="mt-1 text-small text-ink-muted">
            No role has been granted to this account yet, so the ERP has nothing to show you. An administrator grants roles;
            every grant is recorded with who made it and why.
          </p>
        ) : (
          <ul className="mt-1 space-y-0.5">
            {access!.grants.map((grant, i) => (
              <li key={`${grant.role}-${i}`} className="text-small">
                {grant.role_name}
                <span className="text-ink-muted">
                  {' · '}
                  {grant.location_ids.length === 0 ? 'organisation-wide' : `${grant.location_ids.length} location(s) and everything below them`}
                  {grant.valid_to ? ` · until ${fmtDate(grant.valid_to)}` : ''}
                </span>
              </li>
            ))}
          </ul>
        )}
        <p className="mt-1.5 text-small text-ink-faint">
          {(access?.permissions ?? []).length} permissions resolve for this session. They are looked up on the server for every
          request, never read from your sign-in token, so a change takes effect at once.
        </p>
      </section>

      <TwoFactorSection
        required={Boolean(me?.mfa_required)}
        enrolled={Boolean(aal?.hasVerifiedFactor)}
        onChanged={async () => {
          await refresh();
          await queryClient.invalidateQueries({ queryKey: ['my-login-activity'] });
        }}
      />

      <PasswordSection />

      <SessionsSection accountId={me?.user_account_id} open={open} />

      <section className="border-t border-line px-4 py-3">
        <SectionLabel>Recent sign-in activity</SectionLabel>
        {(activity.data ?? []).length === 0 ? (
          <p className="mt-1 text-small text-ink-muted">Nothing recorded yet.</p>
        ) : (
          <ul className="mt-1 space-y-0.5">
            {activity.data!.map((event) => (
              <li key={event.id} className={cx('flex justify-between gap-3 text-small', (event.event === 'FAILED' || event.event === 'LOCKED') && 'text-refused')}>
                <span>{EVENT_WORD[event.event] ?? event.event}</span>
                <span className="tabular text-ink-muted">{fmtDateTime(event.occurred_at)}</span>
              </li>
            ))}
          </ul>
        )}
        <p className="mt-1.5 text-small text-ink-faint">
          A failed attempt you do not recognise is worth telling an administrator about.
        </p>
      </section>
    </Drawer>
  );
}

/* ── Sessions: where this account is signed in, and a way to end the others ───────────────────── */

interface SessionRow {
  session_id: string;
  first_seen_at: string;
  last_seen_at: string;
  user_agent: string | null;
  signed_out: boolean;
  revoked: boolean | null;
}

/** "Chrome on Windows", from a user agent, without pretending to more precision than the string has. */
function describeAgent(agent: string | null): string {
  if (!agent) return 'Unknown browser';
  const browser = /Edg\//.test(agent) ? 'Edge' : /Chrome\//.test(agent) ? 'Chrome' : /Firefox\//.test(agent) ? 'Firefox' : /Safari\//.test(agent) ? 'Safari' : 'Browser';
  const os = /Windows/.test(agent) ? 'Windows' : /Android/.test(agent) ? 'Android' : /iPhone|iPad/.test(agent) ? 'iOS' : /Mac OS X/.test(agent) ? 'macOS' : /Linux/.test(agent) ? 'Linux' : 'unknown system';
  return `${browser} on ${os}`;
}

function SessionsSection({ accountId, open }: { accountId: string | undefined; open: boolean }) {
  const queryClient = useQueryClient();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  const sessions = useQuery({
    queryKey: ['my-sessions', accountId],
    enabled: open && Boolean(accountId),
    queryFn: async () => {
      const { data, error: failure } = await supabase
        .from('user_sessions')
        .select('session_id,first_seen_at,last_seen_at,user_agent,signed_out,revoked')
        .eq('user_account_id', accountId!)
        .order('last_seen_at', { ascending: false })
        .limit(8);
      if (failure) throw failure;
      return data as SessionRow[];
    },
  });

  const endOthers = async () => {
    setBusy(true);
    setError(null);
    try {
      await signOutEverywhere(false);
      setDone(true);
      await queryClient.invalidateQueries({ queryKey: ['my-sessions'] });
      await queryClient.invalidateQueries({ queryKey: ['my-login-activity'] });
    } catch (failure) {
      setError((failure as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const live = (sessions.data ?? []).filter((s) => !s.signed_out && !s.revoked);

  return (
    <section className="border-t border-line px-4 py-3">
      <SectionLabel>Where you are signed in</SectionLabel>
      {live.length === 0 ? (
        <p className="mt-1 text-small text-ink-muted">No open session has been seen yet.</p>
      ) : (
        <ul className="mt-1 space-y-0.5">
          {live.map((s) => (
            <li key={s.session_id} className="flex justify-between gap-3 text-small">
              <span>{describeAgent(s.user_agent)}</span>
              <span className="tabular text-ink-muted">since {fmtDateTime(s.first_seen_at)}</span>
            </li>
          ))}
        </ul>
      )}
      <div className="mt-2 flex items-center gap-2">
        <Button size="dense" onClick={endOthers} disabled={busy}>
          <LogOut size={13} aria-hidden /> Sign out everywhere else
        </Button>
        {done ? <span className="text-small text-ink-muted">Done. Every other session is ended; this one stays.</span> : null}
      </div>
      {error ? <p className="mt-1 text-small text-refused">{error}</p> : null}
      <p className="mt-1.5 text-small text-ink-faint">
        An ended session stops working at its next request, not when its token runs out. Use this after signing in on a
        shared or lost device.
      </p>
    </section>
  );
}

/* ── Two-factor: the person enrols; only an administrator can make it mandatory or release it ────── */

function TwoFactorSection({ required, enrolled, onChanged }: { required: boolean; enrolled: boolean; onChanged: () => Promise<void> }) {
  const [enrolment, setEnrolment] = useState<Enrolment | null>(null);
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const begin = async () => {
    setBusy(true);
    setError(null);
    try {
      setEnrolment(await beginTotpEnrolment());
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const verify = async () => {
    if (!enrolment) return;
    setBusy(true);
    setError(null);
    try {
      await verifyTotp(enrolment.factorId, code);
      setEnrolment(null);
      setCode('');
      await onChanged();
    } catch (e) {
      setError((e as Error).message);
      setCode('');
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="border-t border-line px-4 py-3">
      <div className="flex items-center justify-between gap-3">
        <SectionLabel>Two-factor authentication</SectionLabel>
        {enrolled ? <StatusBadge tone="accepted" icon={ShieldCheck}>Set up</StatusBadge> : null}
      </div>
      <p className="mt-1 text-small text-ink-muted">
        {enrolled
          ? 'An authenticator app is set up for this login.'
          : 'Not set up. You can add an authenticator app now; you do not have to wait to be required to.'}{' '}
        {required
          ? 'An administrator has made it mandatory for this account.'
          : 'It is not mandatory for this account. Only an administrator can change that.'}
      </p>

      {!enrolled && !enrolment ? (
        <Button size="dense" className="mt-2" onClick={() => void begin()} disabled={busy}>
          <ShieldCheck size={13} aria-hidden /> Set up an authenticator app
        </Button>
      ) : null}

      {enrolment ? (
        <div className="mt-3">
          <div className="flex gap-3">
            <img src={enrolment.qrCode} alt="QR code for the authenticator app" width={132} height={132} className="shrink-0 rounded-[var(--radius-control)] border border-line bg-white p-1" />
            <div className="min-w-0 text-small text-ink-muted">
              Scan this with your authenticator app, or enter the key by hand:
              <code className="code-text mt-1 block break-all text-ink">{enrolment.secret}</code>
            </div>
          </div>
          <form
            noValidate
            className="mt-3 flex items-end gap-2"
            onSubmit={(event) => {
              event.preventDefault();
              void verify();
            }}
          >
            <Field label="Six-digit code" className="flex-1">
              <Input inputMode="numeric" autoComplete="one-time-code" maxLength={7} value={code} onChange={(e) => setCode(e.target.value)} className="font-mono tracking-[0.3em]" />
            </Field>
            <Button type="submit" variant="primary" disabled={busy || code.replace(/\s/g, '').length !== 6}>
              {busy ? 'Checking…' : 'Verify'}
            </Button>
          </form>
        </div>
      ) : null}

      {error ? <p role="alert" className="mt-2 text-small text-refused">{error}</p> : null}
    </section>
  );
}

/* ── Password ────────────────────────────────────────────────────────────────────────────────────── */

function PasswordSection() {
  const [open, setOpen] = useState(false);
  const [password, setPasswordValue] = useState('');
  const [confirm, setConfirm] = useState('');
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<{ ok: boolean; text: string } | null>(null);

  const problem = passwordProblem(password);
  const mismatch = confirm.length > 0 && confirm !== password;

  return (
    <section className="border-t border-line px-4 py-3">
      <SectionLabel>Password</SectionLabel>
      {!open ? (
        <Button size="dense" className="mt-2" onClick={() => { setOpen(true); setMessage(null); }}>
          <KeyRound size={13} aria-hidden /> Change password
        </Button>
      ) : (
        <form
          noValidate
          className="mt-2 space-y-3"
          onSubmit={async (event) => {
            event.preventDefault();
            setBusy(true);
            setMessage(null);
            try {
              await setPassword(password);
              setMessage({ ok: true, text: 'Password changed. Other devices stay signed in until their session ends.' });
              setOpen(false);
              setPasswordValue('');
              setConfirm('');
            } catch (e) {
              setMessage({ ok: false, text: (e as Error).message });
            } finally {
              setBusy(false);
            }
          }}
        >
          <Field label="New password" required error={password.length > 0 ? problem : null} hint="At least 12 characters, with a letter and a number.">
            <Input type="password" autoComplete="new-password" value={password} onChange={(e) => setPasswordValue(e.target.value)} />
          </Field>
          <Field label="Confirm" required error={mismatch ? 'The two passwords differ.' : null}>
            <Input type="password" autoComplete="new-password" value={confirm} onChange={(e) => setConfirm(e.target.value)} />
          </Field>
          <div className="flex justify-end gap-2">
            <Button type="button" size="dense" onClick={() => setOpen(false)} disabled={busy}>Cancel</Button>
            <Button type="submit" size="dense" variant="primary" disabled={busy || Boolean(problem) || mismatch || confirm.length === 0}>
              {busy ? 'Saving…' : 'Change password'}
            </Button>
          </div>
        </form>
      )}
      {message ? (
        <p role="status" className={cx('mt-2 text-small', message.ok ? 'text-accepted' : 'text-refused')}>{message.text}</p>
      ) : null}
    </section>
  );
}
